// src/services/appointmentService.ts
import { AxiosError } from 'axios';
import api from '../api/apiClient';
import { Appointment, CreateAppointmentData, CheckInData, CheckInEligibility } from '../types/appointment';
import { Location } from '../types/auth';

export const appointmentService = {
  // ...other methods remain unchanged...

  getAppointments: async (): Promise<Appointment[]> => {
    try {
      // add a timestamp query param to bust any cache
      const response = await api.getWithParams<{
        data: Appointment[];
      }>('/appointments', {
        // your API client only allows params here — so we use "cb" as a cache‐buster
        cb: Date.now().toString(),
      });
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get appointments error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  getAppointment: async (id: string): Promise<Appointment> => {
    try {
      const response = await api.get(`/appointments/${id}`);
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get appointment error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  createAppointment: async (
    appointmentData: CreateAppointmentData
  ): Promise<Appointment> => {
    try {
      const response = await api.postWithData<{ data: Appointment }>(
        '/appointments',
        appointmentData
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Create appointment error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  updateAppointmentStatus: async (
    id: string,
    data: { status: string; reason?: string; location?: Location }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/status`,
        data
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Update status error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  checkIn: async (id: string, data: CheckInData): Promise<Appointment> => {
    try {
      const response = await api.postWithData<{ data: Appointment }>(
        `/appointments/${id}/check-in`,
        data
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Check-in error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  getCheckInEligibility: async (id: string): Promise<CheckInEligibility> => {
    try {
      const response = await api.get(`/appointments/${id}/check-in/eligibility`);
      return (response.data as { data: CheckInEligibility }).data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Check-in eligibility error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  requestCheckInPin: async (id: string): Promise<{ expiresAt: string }> => {
    try {
      const response = await api.postWithData<{ data: { expiresAt: string } }>(
        `/appointments/${id}/check-in/pin-request`,
        {}
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Request check-in PIN error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  getCheckInPin: async (id: string): Promise<{ pin: string; expiresAt: string }> => {
    try {
      const response = await api.get(`/appointments/${id}/check-in/pin`);
      return (response.data as { data: { pin: string; expiresAt: string } }).data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get check-in PIN error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  verifyCheckInPin: async (id: string, pin: string): Promise<Appointment> => {
    try {
      const response = await api.postWithData<{ data: Appointment }>(
        `/appointments/${id}/check-in/verify-pin`,
        { pin }
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Verify check-in PIN error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  generatePin: async (id: string): Promise<{ pin: string; expiresAt: string }> => {
    try {
      const response = await api.postWithData<{
        data: { pin: string; expiresAt: string };
      }>(`/appointments/${id}/generate-pin`, {});
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Generate PIN error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  startService: async (
    id: string,
    location?: { coordinates: [number, number]; accuracy: number }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/start`,
        { location }
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Start service error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  completeService: async (
    id: string,
    location?: { coordinates: [number, number]; accuracy: number }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/complete`,
        { location }
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Complete service error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  confirmCompletion: async (id: string): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/confirm-completion`,
        {}
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Confirm completion error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  markNoShow: async (
    id: string,
    reason?: string,
    location?: { coordinates: [number, number]; accuracy: number }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/mark-no-show`,
        { notes: reason }
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Mark no-show error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  // No-Show Management (New API)
  markAsNoShow: async (id: string, notes?: string): Promise<Appointment> => {
    try {
      const response = await api.put(`/appointments/${id}/mark-no-show`, { notes });
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Mark as no-show error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  getNoShowSummary: async (id: string): Promise<{
    canMarkAsNoShow: boolean;
    reason?: string;
    minutesLate: number;
    financial: {
      downPaymentRetained: number;
      stylistCompensation: number;
      platformFee: number;
    };
    policy: {
      gracePeriod: string;
      retentionPolicy: string;
      additionalCharges: string;
    };
  }> => {
    try {
      const response = await api.get(`/appointments/${id}/no-show-summary`);
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get no-show summary error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  requestRunningLate: async (
    id: string,
    estimatedMinutes: number
  ): Promise<Appointment> => {
    try {
      const response = await api.postWithData<{ data: Appointment }>(
        `/appointments/${id}/running-late`,
        { estimatedMinutes }
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Request running late error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  respondToRunningLate: async (
    id: string,
    response: 'accepted' | 'declined'
  ): Promise<Appointment> => {
    try {
      const apiResponse = await api.put(`/appointments/${id}/running-late`, {
        response,
      });
      return apiResponse.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Respond to running late error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  /**
   * Get cancellation summary (charge preview)
   * Shows the cancellation fee (if any) before the client confirms.
   */
  getCancellationSummary: async (id: string): Promise<{
    canCancel: boolean;
    reason?: string;
    appointmentStatus?: string;
    hoursUntilAppointment?: number;
    refundAmount?: number;
    refundPercentage?: number;
    cancellationFee?: number;
    cancellationFeePercentage?: number;
    gracePeriodApplied?: boolean;
    feeDistribution?: { stylist: number; business: number };
    appliedRule?: string;
    message?: string;
    warning?: string;
    policy?: any;
  }> => {
    try {
      const response = await api.get(
        `/appointments/${id}/cancellation-summary`
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get cancellation summary error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  cancelAppointment: async (
    id: string,
    data: { reason: string; notes?: string }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/cancel`,
        data
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Cancel appointment error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  rejectAppointment: async (
    id: string,
    data: { reason: string; notes?: string }
  ): Promise<Appointment> => {
    try {
      const response = await api.putWithData<{ data: Appointment }>(
        `/appointments/${id}/reject`,
        data
      );
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Reject appointment error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  canCancel: (appointment: Appointment): boolean => {
    return (
      ['pending', 'confirmed'].includes(appointment.status) &&
      new Date(appointment.dateTime) > new Date()
    );
  },

  canReschedule: (appointment: Appointment): boolean => {
    return (
      ['pending', 'confirmed'].includes(appointment.status) &&
      new Date(appointment.dateTime) > new Date()
    );
  },

  canMarkAsNoShow: (appointment: Appointment): boolean => {
    // Can mark as no-show if:
    // 1. Status is 'confirmed' or 'checked-in'
    // 2. At least 20 minutes have passed since appointment start time
    if (!['confirmed', 'checked-in'].includes(appointment.status)) {
      return false;
    }

    const appointmentTime = new Date(appointment.dateTime);
    const now = new Date();
    const minutesLate = Math.floor(
      (now.getTime() - appointmentTime.getTime()) / (1000 * 60)
    );

    return minutesLate >= 20;
  },

  // Add-on Management
  addAppointmentAddOns: async (
    appointmentId: string,
    addOns: Array<{
      sourceAddOnId?: string;
      sourceType: 'predefined' | 'custom';
      name: string;
      description?: string;
      priceCents: number;
      durationMinutes: number;
    }>
  ): Promise<Appointment> => {
    try {
      const response = await api.patch(`/appointments/${appointmentId}/add-ons`, { addOns });
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Add appointment add-ons error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  removeAppointmentAddOn: async (
    appointmentId: string,
    addOnId: string
  ): Promise<Appointment> => {
    try {
      const response = await api.delete(`/appointments/${appointmentId}/add-ons/${addOnId}`);
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Remove appointment add-on error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },

  getAppointmentAddOns: async (appointmentId: string): Promise<any> => {
    try {
      const response = await api.get(`/appointments/${appointmentId}/add-ons`);
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      console.error(
        'Get appointment add-ons error:',
        axiosError.response?.data || axiosError.message
      );
      throw axiosError;
    }
  },
};

export default appointmentService;
